/** @jsxRuntime classic */
/** @jsx jsx */
import React, { Fragment, useCallback, useEffect, useState, useMemo } from 'react';
import { jsx, css } from '@emotion/react';
import { SectionContainer } from '../sectionContainer';
import { Table } from '../../table';
import { Typography } from '../../../atoms';
import { AlignType, IColumn } from '../../table/types';
import { useWindowSize } from '../../../hooks/useWindowSize';
import {
	EInvoiceVariant,
	IInvoiceTemplateLabels,
	IPaymentDisplayInfo,
	IPaymentDisplayInfoWithActionIds,
	PaymentStatusEnum,
} from '../../../organisms/invoiceTemplate/types';
import { IPaymentMethodDisplayInfo } from '../types';
import { Summary } from '../summary';
import { BACKOFFICE_VARIANTS, EInvoiceAction, EInvoicingTabs } from '../types';
import { SummaryLine } from '../summary/summaryLine';
import { roundPriceCents } from '@bondsports/general';
import { Pricify } from '../../../lib/price';
import { ETypography, ETypographyColor, Icons } from '../../../types';
import { SkeletonWrapper } from '../../../components/skeletonWrapper';
import { BnIcon } from '../../../components/icons_v2';
import { flexRowCss, iconCss } from '../../../organisms/refundPopup/style';
import { oldThemeColors as colors } from '../../../styles/theme';
import { gapCss } from '../../../styles/utils';
import { Column, Row } from '../../../atoms/layoutPrimitives';
import { PaymentMethodMapper } from './PaymentMethodMapper';
import { useDetectPrint } from '../../../hooks/useIsPrint';
import { hrCss } from '../../../organisms/invoiceTemplate/styles';
import { formatDateTimeTz } from '../utils/date-formatting.util';
import { TooltipWithButton } from '../../tooltip-with-button';
import { EmptyCell } from '../EmptyCell';

const PAYMENT_VARIANTS = [EInvoiceVariant.PAYMENT, EInvoiceVariant.PAYMENT_PRINT];

interface IFeeItem {
	fee?: { type?: string; percentageValue?: number; fixValue?: number; name?: string };
}

const buildFeesAndTipsTooltipContent = (
	fees: IFeeItem[] | undefined,
	tipAmount: number,
	labels: { fixed: string; tip?: string }
): string => {
	const fee = fees?.find((f: IFeeItem) => f.fee?.type !== 'Tip') as
		| { fee?: { percentageValue?: number; fixValue?: number; name?: string } }
		| undefined;
	const percent =
		Number(fee?.fee?.percentageValue ?? 0) > 0
			? `${roundPriceCents(Number(fee?.fee?.percentageValue ?? 0) * 100)}%`
			: '';
	const addition = Number(fee?.fee?.percentageValue ?? 0) > 0 && Number(fee?.fee?.fixValue ?? 0) > 0 ? ` + ` : '';
	const fixed =
		Number(fee?.fee?.fixValue ?? 0)
			? `${Pricify(Number(fee?.fee?.fixValue ?? 0), undefined, undefined, true)} ${labels.fixed}`
			: '';
	const feePart = fee ? `${fee?.fee?.name ?? ''} - ${percent}${addition}${fixed}` : '';
	const tipPart =
		Number(tipAmount)
			? (feePart ? ` + ` : '') + `${Pricify(tipAmount, undefined, undefined, true)} ${labels.tip ?? 'tip'}`
			: '';
	if (feePart && tipPart) return `${feePart} ${tipPart}`;
	if (feePart) return feePart;
	if (tipPart) return tipPart;
	return '';
};

//TODO: need to update typing for this file and other relevant files related to this one, see BOND-13658
export const Payments = ({
	labels,
	additional,
	isLoading,
	activeTab,
	additionalInfoRow,
	state,
	variant,
	onAction,
	organizationTimeZone = 'utc',
}: {
	labels: IInvoiceTemplateLabels;
	additional: { payments: IPaymentDisplayInfo[] };
	isLoading: boolean;
	activeTab: string;
	additionalInfoRow?: string;
	state: any;
	variant: EInvoiceVariant;
	onAction: (action: EInvoiceAction, value: any) => void;
	organizationTimeZone?: string;
}) => {
	const [rowsToDisplay, setRowsToDisplay] = useState<IPaymentDisplayInfoWithActionIds[]>([]);
	const [showFeesCol, setShowFeesCol] = useState(false);
	const [showTipsCol, setShowTipsCol] = useState(false);
	const { isMobile } = useWindowSize();
	const isPrint = useDetectPrint();
	const { formatDate } = formatDateTimeTz(organizationTimeZone);
	const invoiceState = state.invoice;

	const canDownloadPayment = BACKOFFICE_VARIANTS.includes(variant) || variant === EInvoiceVariant.CONSUMER;

	const paymentsToDisplay: IPaymentDisplayInfo[] = useMemo(() => {
		if (!additional.payments) return [];

		return additional.payments.map(payment => ({
			...payment,
			method: labels?.paymentMethods?.[payment.method] ?? payment.method,
		}));
	}, [additional.payments, labels]);

	const tableLabels = labels.paymentsSection.table.columns;

	const styling = { align: AlignType.LEFT };
	const columns: IColumn[] = [
		...(!PAYMENT_VARIANTS.includes(variant) ? [{
			id: 'id',
			type: 'string',
			label: tableLabels.id,
			styling,
		} as IColumn] : []),
		{
			id: 'customerName',
			type: 'string',
			label: tableLabels.customerName,
			styling,
		},
		{
			id: 'date',
			type: 'custom',
			label: tableLabels.date,
			component: (state: string) => {
				return (
					<SkeletonWrapper isLoading={isLoading} minHeight={6} minWidth={50}>
						<Typography type={ETypography.body2} color={ETypographyColor.primary}>
							{formatDate(state)}
						</Typography>
					</SkeletonWrapper>
				);
			},
			styling,
		},
		{
			id: 'priceWithoutFee',
			type: 'currency',
			label: tableLabels.amount,
			styling,
		},
		...(showFeesCol || showTipsCol
			? PAYMENT_VARIANTS.includes(variant)
				? [
						...(showFeesCol
							? [
									{
										id: 'paymentFees',
										type: 'custom',
										label: tableLabels.fees,
										styling,
										component: (state?: { fees?: unknown[] }) => {
											const fee = state?.fees?.find((f: any) => f?.fee?.type !== 'tip') as
												| { feeAmount: number; fee?: { percentageValue?: number; fixValue?: number; name?: string } }
												| undefined;
											const tooltipContent = buildFeesAndTipsTooltipContent(state?.fees as IFeeItem[] | undefined, 0, {
												fixed: labels.paymentsSection.fees.fixed,
											});
											return (
												<TooltipWithButton showButton={!isPrint} icon={Icons.info} tooltipContent={tooltipContent}>
													<Typography type={ETypography.body2} color={ETypographyColor.primary}>
														{Pricify(fee?.feeAmount ?? 0, undefined, undefined, true)}
													</Typography>
												</TooltipWithButton>
											);
										},
									} as IColumn,
								]
							: []),
						...(showTipsCol
							? [
									{
										id: 'paymentTips',
										type: 'custom',
										label: tableLabels.tip,
										styling,
										component: (state?: { tipAmount?: number }) => {
											return (
												<Typography type={ETypography.body2} color={ETypographyColor.primary}>
													{Pricify(state?.tipAmount ?? 0, undefined, undefined, true)}
												</Typography>
											);
										},
									} as IColumn,
								]
							: []),
					]
				: ([
						{
							id: 'paymentFees',
							type: 'custom',
							label: showFeesCol && showTipsCol ? tableLabels.extras : showFeesCol ? tableLabels.fees : tableLabels.tip,
							styling,
							component: (state?: { fees?: unknown[]; tipAmount?: number }) => {
								const tooltipContent = buildFeesAndTipsTooltipContent(
									state?.fees as IFeeItem[] | undefined,
									state?.tipAmount ?? 0,
									{
										fixed: labels.paymentsSection.fees.fixed,
										tip: labels.paymentsSection?.fees?.tip,
									}
								);

								const totalFeeAmount =
									(state?.fees as { feeAmount?: number }[] | undefined)?.reduce(
										(acc, current) => acc + (current?.feeAmount ?? 0),
										0
									) ?? 0;

								return totalFeeAmount ? (
									<TooltipWithButton showButton={!isPrint} icon={Icons.info} tooltipContent={tooltipContent}>
										<Typography type={ETypography.body2} color={ETypographyColor.primary}>
											{Pricify(totalFeeAmount, undefined, undefined, true)}
										</Typography>
									</TooltipWithButton>
								) : (
									<EmptyCell />
								);
							},
						},
					] as IColumn[])
			: []),
		{
			id: 'amount',
			type: 'currency',
			label: tableLabels.total,
			styling,
		},
		{
			id: 'method',
			type: 'paymentMethod',
			label: tableLabels.method,
			styling,
		},

		{
			id: 'status',
			type: 'status',
			label: tableLabels.status,
			styling,
		},
		...(canDownloadPayment
			? ([
					{
						id: 'view',
						type: 'custom',
						label: '',
						styling: { align: AlignType.RIGHT },
						component: (paymentId: number) => (
							<div data-aid="invoicePayment" css={[flexRowCss, gapCss(10)]}>
								<div
									css={iconCss(colors.brandTertiary, 20, '0', colors.brandPrimary)}
									onClick={() => onAction(EInvoiceAction.VIEW, paymentId)}
								>
									<BnIcon icon={Icons.view} />
								</div>
								<div
									css={iconCss(colors.brandTertiary, 20, '0', colors.brandPrimary)}
									onClick={() => onAction(EInvoiceAction.DOWNLOAD, paymentId)}
								>
									<BnIcon icon={Icons.download} />
								</div>
							</div>
						),
					},
				] as IColumn[])
			: []),
	];

	const filterColumns = columns.filter(col => {
		if (isMobile) {
			if (col.id === 'method') {
				return false;
			}
		}
		if (!showFeesCol) {
			if (col.id === 'feesAmount') {
				return false;
			}
		}
		if (!invoiceState?.isPublic) {
			if (col.id === 'customerName') {
				return false;
			}
		}
		return true;
	});

	useEffect(() => {
		const paymentsWithActionIds = paymentsToDisplay.map((p: IPaymentDisplayInfo) => ({
			...p,
			paymentFees: { fees: p.paymentFees, tipAmount: p.tipAmount ?? 0 },
			paymentTips: { tipAmount: p.tipAmount ?? 0 },
			view: p.id,
			download: p.id,
		}));
		setRowsToDisplay(paymentsWithActionIds as unknown as IPaymentDisplayInfoWithActionIds[]);
		setShowFeesCol(paymentsToDisplay.some((p: IPaymentDisplayInfo) => p.feesAmount));
		setShowTipsCol(paymentsToDisplay.some((p: IPaymentDisplayInfo) => (p.tipAmount ?? 0)));
	}, [invoiceState, paymentsToDisplay]);

	const SummaryComponent = useCallback(
		() => (
			<Summary gap={variant === EInvoiceVariant.BACKOFFICE ? 8 : 12} className="no-print">
				{(invoiceState?.totalFeesAmount ?? 0) > 0 ? (
					<SummaryLine
						label={labels?.paymentsSection?.summary?.fees}
						value={Pricify(invoiceState?.totalFeesAmount ?? 0, undefined, undefined, true)}
						isLoading={isLoading}
						variant={variant}
					/>
				) : (
					<Fragment />
				)}
				{(invoiceState?.totalTipsAmount ?? 0) > 0 ? (
					<SummaryLine
						label={labels?.paymentsSection?.summary?.tips}
						value={Pricify(invoiceState?.totalTipsAmount ?? 0, undefined, undefined, true)}
						isLoading={isLoading}
						variant={variant}
					/>
				) : (
					<Fragment />
				)}
				<SummaryLine
					label={labels?.paymentsSection?.summary?.amountPaid}
					value={Pricify(invoiceState?.paidAmount, undefined, undefined, true)}
					isLoading={isLoading}
					variant={variant}
					isMain={invoiceState?.paymentStatus === PaymentStatusEnum.FULLY_PAID}
				/>
				{invoiceState?.paymentStatus === PaymentStatusEnum.NOT_PAID ||
				invoiceState?.paymentStatus === PaymentStatusEnum.PARTIAL_PAYMENT ? (
					<SummaryLine
						label={labels?.paymentsSection?.summary?.balanceDue}
						value={Pricify(invoiceState?.meta?.totalDue, undefined, undefined, true)}
						isLoading={isLoading}
						variant={variant}
						isMain
					/>
				) : (
					<Fragment />
				)}
			</Summary>
		),
		[invoiceState, isLoading, labels, variant]
	);

	return (
		<Fragment>
			{activeTab === EInvoicingTabs.SCHEDULE && !isPrint ? (
				<Fragment />
			) : (
				<Fragment>
					<div className="no-print">
						{paymentsToDisplay.length > 0 && (
							<SectionContainer title={labels.paymentsSection.title}>
								<Table<any>
									columns={filterColumns}
									rows={rowsToDisplay}
									pagination={false}
									isLoading={isLoading}
									isHover={false}
									additionalInfoRow={additionalInfoRow}
									footer={!PAYMENT_VARIANTS.includes(variant) && <SummaryComponent />}
									maxHeight={-1}
								/>
							</SectionContainer>
						)}
					</div>
					{Boolean(paymentsToDisplay[0]) && (
						<Column fullWidth className="print">
							<Row fullWidth justify="space-between">
								<Typography color={ETypographyColor.primary} type={ETypography.body2}>
									{labels.paymentsSection.payment}
								</Typography>
								<Typography color={ETypographyColor.primary} type={ETypography.body2Accented}>
									{Pricify(paymentsToDisplay[0].amount - paymentsToDisplay[0].feesAmount, undefined, undefined, true)}
								</Typography>
							</Row>
							{(paymentsToDisplay[0].feesAmount ?? 0) + (paymentsToDisplay[0].tipAmount ?? 0) > 0 && (
								<Row fullWidth justify="space-between">
									<Typography color={ETypographyColor.primary} type={ETypography.body2}>
										{labels.paymentsSection.table.columns.fees}
									</Typography>
									<Typography color={ETypographyColor.primary} type={ETypography.body2}>
										{Pricify(
											(paymentsToDisplay[0].feesAmount ?? 0) + (paymentsToDisplay[0].tipAmount ?? 0),
											undefined,
											undefined,
											true
										)}
									</Typography>
								</Row>
							)}
							<hr css={hrCss} />
							<Row fullWidth justify="space-between">
								<Column align="start" gap="0">
									<Typography color={ETypographyColor.primary} type={ETypography.body2}>
										{labels.paymentsSection.summary.totalPaid}
									</Typography>
									<PaymentMethodMapper state={paymentsToDisplay[0] as IPaymentMethodDisplayInfo} noIcons />
								</Column>
								<Typography color={ETypographyColor.primary} type={ETypography.body2}>
									{Pricify(paymentsToDisplay[0].amount, undefined, undefined, true)}
								</Typography>
							</Row>
							<Row
								fullWidth
								justify="center"
								styles={css`
									margin-top: 5rem;
								`}
							>
								<Typography color={ETypographyColor.primary} type={ETypography.body1Accented}>
									{labels.paymentsSection.thankYou}
								</Typography>
							</Row>
						</Column>
					)}
				</Fragment>
			)}
		</Fragment>
	);
};
